/**
 * Cube auto-transfer rules: which agents are dummy (receive) vs active (destination).
 *
 * Config: auto-call-transfer-config.json → cube.campaigns (or S3 when enabled).
 * Flow: dummy agent on login campaign receives call → pick online activeUser → blind transfer.
 * Only dialerUser strings from JSON are used at runtime.
 */
import {
  AUTO_CALL_TRANSFER_USE_S3,
  extractDialerUsers,
  getCubeCampaignsConfig,
  initAutoCallTransferConfig,
} from '../../../helper/dialersConfigs/auto-call-transfer-config';

export interface CubeCampaignTransferRule {
  dummyUsers: string[];
  targetCampaign: string;
  activeUsers: string[];
}

export interface CubeAutoTransferConfig {
  campaigns: Record<string, CubeCampaignTransferRule>;
}

export interface CubeAutoTransferProfile {
  enabled: boolean;
  targetAgentId: string;
  targetCampaign: string;
  loginCampaign: string;
}

interface StoredCampaignRule {
  campaignName: string;
  rule: CubeCampaignTransferRule;
}

const campaignRules = new Map<string, StoredCampaignRule>();

function normalizeId(value: string): string {
  return String(value ?? '').trim().toLowerCase();
}

function normalizeCampaignKey(value: string): string {
  const raw = String(value ?? '').trim();
  if (!raw) {
    return '';
  }
  return raw.split('~')[0].trim().toLowerCase();
}

function normalizeUserList(value: unknown): string[] {
  return extractDialerUsers(value);
}

function resolveCampaignRule(campaignName: string): StoredCampaignRule | null {
  const key = normalizeCampaignKey(campaignName);
  if (!key) {
    return null;
  }
  return campaignRules.get(key) || null;
}

function applyConfig(raw: unknown): void {
  campaignRules.clear();
  const config = raw as Partial<CubeAutoTransferConfig> | null | undefined;
  const campaigns = config?.campaigns;
  if (!campaigns || typeof campaigns !== 'object' || Array.isArray(campaigns)) {
    return;
  }
  for (const [campaignName, entry] of Object.entries(campaigns)) {
    const name = String(campaignName ?? '').trim();
    if (!name) {
      continue;
    }
    const rule: CubeCampaignTransferRule = {
      dummyUsers: normalizeUserList(entry?.dummyUsers),
      targetCampaign: String(entry?.targetCampaign ?? name).trim() || name,
      activeUsers: normalizeUserList(entry?.activeUsers),
    };
    if (!rule.targetCampaign && rule.activeUsers.length === 0 && rule.dummyUsers.length === 0) {
      continue;
    }
    campaignRules.set(normalizeCampaignKey(name), { campaignName: name, rule });
  }
}

function loadCubeConfig(): void {
  applyConfig(getCubeCampaignsConfig());
}

loadCubeConfig();
void initAutoCallTransferConfig().then(() => {
  if (AUTO_CALL_TRANSFER_USE_S3) {
    loadCubeConfig();
  }
});

export function getCubeAutoTransferCampaigns(): readonly string[] {
  return Array.from(campaignRules.values()).map((entry) => entry.campaignName);
}

export function isCubeAutoTransferConfigReady(): boolean {
  for (const { rule } of campaignRules.values()) {
    if (rule.targetCampaign && rule.activeUsers.length > 0) {
      return true;
    }
  }
  return false;
}

export function isCubeAutoTransferSourceCampaign(campaignName: string): boolean {
  return !!resolveCampaignRule(campaignName);
}

export function getCubeAutoTransferTargetCampaign(campaignName: string): string {
  return resolveCampaignRule(campaignName)?.rule.targetCampaign || '';
}

/** Logged-in agent is listed as a dummy receiver for this campaign. */
export function isCubeAutoTransferDummyAgent(agentId: string, campaignName: string): boolean {
  const needle = normalizeId(agentId);
  if (!needle) {
    return false;
  }
  const stored = resolveCampaignRule(campaignName);
  if (!stored || stored.rule.dummyUsers.length === 0) {
    return false;
  }
  return stored.rule.dummyUsers.some((id) => normalizeId(id) === needle);
}

/** Agent + campaign qualify for auto-transfer (does not require a picked target yet). */
export function isCubeAutoTransferDummyEligible(agentId: string, loginCampaign: string): boolean {
  const campaignKey = normalizeCampaignKey(loginCampaign);
  if (!campaignKey || !isCubeAutoTransferConfigReady()) {
    return false;
  }
  const stored = resolveCampaignRule(loginCampaign);
  if (!stored) {
    return false;
  }
  const agent = String(agentId ?? '').trim();
  return !!agent && isCubeAutoTransferDummyAgent(agent, stored.campaignName);
}

/**
 * Pick a real (activeUser) agent to receive the dummy's campaign call.
 * Only agents that appear in onlineAgentIds are eligible — idle/ready in Cube is not enough
 * if getCubeCampaignAgents did not return that agent.
 */
export function pickCubeAutoTransferTarget(
  onlineAgentIds: string[] | undefined,
  campaignName: string
): string | null {
  const stored = resolveCampaignRule(campaignName);
  if (!stored) {
    return null;
  }
  const active = stored.rule.activeUsers.map((id) => String(id ?? '').trim()).filter(Boolean);
  if (!active.length) {
    return null;
  }
  if (onlineAgentIds === undefined) {
    return null;
  }
  const onlineList = onlineAgentIds.map((id) => String(id ?? '').trim()).filter(Boolean);
  const onlineSet = new Set(onlineList.map((id) => normalizeId(id)).filter(Boolean));
  if (onlineSet.size === 0) {
    console.log('[CubeAutoTransfer] pick target skipped — campaign agent API returned nobody online', {
      campaignName,
      activeUsers: active,
      onlineAgentIds: onlineList,
    });
    return null;
  }
  const onlineActive = active.filter((id) => onlineSet.has(normalizeId(id)));
  if (onlineActive.length === 0) {
    console.log('[CubeAutoTransfer] pick target skipped — real user not in online list (idle Cube agent still needs to be in this API list)', {
      campaignName,
      activeUsers: active,
      onlineAgentIds: onlineList,
    });
    return null;
  }
  const picked = onlineActive[Math.floor(Math.random() * onlineActive.length)] || null;
  console.log('[CubeAutoTransfer] picked real user', {
    campaignName,
    picked,
    onlineActive,
    activeUsers: active,
    onlineAgentIds: onlineList,
  });
  return picked;
}

const emptyProfile: CubeAutoTransferProfile = {
  enabled: false,
  targetAgentId: '',
  targetCampaign: '',
  loginCampaign: '',
};

/**
 * Builds transfer profile for a dummy session.
 * Pass onlineAgentIds from fetchCubeAgentsForCampaign — without them enabled stays false.
 */
export function resolveCubeAutoTransferProfile(
  agentId: string,
  loginCampaign: string,
  onlineAgentIds?: string[]
): CubeAutoTransferProfile {
  const campaignKey = normalizeCampaignKey(loginCampaign);
  if (!campaignKey || !isCubeAutoTransferConfigReady()) {
    return emptyProfile;
  }
  const stored = resolveCampaignRule(loginCampaign);
  if (!stored) {
    return emptyProfile;
  }
  const agent = String(agentId ?? '').trim();
  if (!agent || !isCubeAutoTransferDummyAgent(agent, stored.campaignName)) {
    return emptyProfile;
  }
  const targetCampaign = stored.rule.targetCampaign || stored.campaignName;
  const targetAgentId =
    pickCubeAutoTransferTarget(onlineAgentIds, stored.campaignName) || '';
  const profile = {
    enabled: !!targetCampaign && !!targetAgentId,
    targetAgentId,
    targetCampaign,
    loginCampaign: stored.campaignName,
  };
  if (onlineAgentIds !== undefined) {
    console.log('[CubeAutoTransfer] profile after online pick', {
      ...profile,
      dummyAgentId: agent,
      passedOnlineIds: onlineAgentIds,
    });
  }
  return profile;
}
